<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  erpIntegrationApi, type FeedingLine, type IncorporatedPatient, type PatientSalesOrder,
} from '@/api/erp-integration';
import { apiErrorCode } from '@/utils/api-errors';
import { todayIso } from '@/utils/calendar-events';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import { actionError } from './erp-messages';
import { formatDate, formatRange, quantityText, requirementText } from './erp-format';

// "Pacientes incorporados" (ADR 0001, "Moving a Go-live date"): since when
// Kuido sends each patient's Sessions, and which Sales order line feeds each of
// its Requirements, so which line its Shifts' Sessions count on in the ERP.

const emit = defineEmits<{ changed: [message?: string] }>();

const { data, isPending } = useQuery({
  queryKey: ['erpIntegration', 'patients'],
  queryFn: erpIntegrationApi.incorporatedPatients,
});

const search = ref('');
const patients = computed(() => {
  const term = (search.value ?? '').trim().toLowerCase();
  const rows = data.value ?? [];

  return term ? rows.filter((row) => row.name.toLowerCase().includes(term)) : rows;
});

const today = todayIso();

// Why a line takes no Sessions, whatever Requirements it fed.
function lineState(line: FeedingLine): string | null {
  if (line.dismissed) return 'Descartada';
  if (line.state === 'removed') return 'Quitada del presupuesto';
  if (line.state && line.state !== 'sale') return 'Cancelada en Odoo';
  if (line.kind === 'unmapped') return 'Producto sin mapear';

  return null;
}

function orderState(order: PatientSalesOrder): string | null {
  if (order.state && order.state !== 'sale') return 'Cancelado en Odoo';
  if (order.endsOn && order.endsOn < today) return 'Terminado';

  return null;
}

const moving = ref<IncorporatedPatient | null>(null);
const goLiveOn = ref('');
const moveError = ref('');

function openMove(patient: IncorporatedPatient) {
  moving.value = patient;
  goLiveOn.value = patient.goLiveOn;
  moveError.value = '';
}

const dateError = computed(() => (
  goLiveOn.value && goLiveOn.value >= today ? '' : 'Elige hoy o una fecha posterior.'
));
const direction = computed(() => {
  if (!moving.value || dateError.value || goLiveOn.value === moving.value.goLiveOn) return null;

  return goLiveOn.value > moving.value.goLiveOn ? 'later' : 'earlier';
});

const { mutate: move, isPending: isMoving } = useMutation({
  mutationFn: ({ id, date }: { id: number; date: string }) => erpIntegrationApi.moveGoLive(id, date),
  onSuccess: () => {
    moving.value = null;
    emit('changed', '¡Fecha de incorporación actualizada!');
  },
  onError: (error) => {
    moveError.value = actionError(apiErrorCode(error),
      'Ocurrió un error al cambiar la fecha. Intenta nuevamente.');
  },
});
</script>
<template>
  <div class="pa-4">
    <h2 class="text-h6 mb-1">
      Pacientes incorporados
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      Desde su fecha de incorporación, Kuido envía a Odoo las sesiones de cada paciente. Cada línea
      de sus presupuestos alimenta los requerimientos que aparecen bajo ella: los turnos de esos
      requerimientos llegan a Odoo como sesiones de esa línea.
    </p>
    <v-text-field
      v-model="search"
      label="Buscar paciente"
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      density="compact"
      clearable
      hide-details
      class="patients-search mb-4"
    />
    <div
      v-if="isPending"
      class="d-flex justify-center pa-6"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        aria-label="Cargando los pacientes..."
      />
    </div>
    <p
      v-else-if="!patients.length"
      class="text-body-2 text-medium-emphasis"
    >
      {{ search ? 'No hay pacientes con ese nombre' : 'No hay pacientes incorporados' }}
    </p>
    <v-expansion-panels
      v-else
      multiple
      variant="accordion"
    >
      <v-expansion-panel
        v-for="patient in patients"
        :key="patient.id"
        :value="patient.id"
      >
        <v-expansion-panel-title>
          <div class="d-flex flex-wrap align-center ga-3">
            <span class="font-weight-medium">{{ patient.name }}</span>
            <span class="text-body-2 text-medium-emphasis">
              Desde el {{ formatDate(patient.goLiveOn) }} ·
              {{ patient.orders.length }} {{ patient.orders.length === 1 ? 'presupuesto' :
                'presupuestos' }}
            </span>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="d-flex flex-wrap align-center ga-2 mb-4">
            <span class="text-body-2">
              Kuido envía sus sesiones a Odoo desde el
              <strong>{{ formatDate(patient.goLiveOn) }}</strong>.
            </span>
            <v-btn
              v-if="patient.goLiveMovable"
              size="small"
              variant="tonal"
              color="primary"
              prepend-icon="mdi-calendar-edit"
              @click="openMove(patient)"
            >
              Cambiar fecha
            </v-btn>
            <span
              v-else
              class="text-caption text-medium-emphasis"
            >
              Esa fecha ya llegó, así que no se puede cambiar.
            </span>
            <v-spacer />
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :href="`/patients/${patient.id}`"
            >
              Ver paciente
            </v-btn>
          </div>
          <v-card
            v-for="order in patient.orders"
            :key="order.id"
            flat
            class="border mb-3"
          >
            <v-card-text>
              <div class="d-flex flex-wrap align-center ga-2 mb-2">
                <span class="font-weight-bold">Presupuesto {{ order.reference ?? '—' }}</span>
                <span class="text-body-2 text-medium-emphasis">
                  {{ formatRange(order.startsOn, order.endsOn) }}
                </span>
                <v-chip
                  v-if="orderState(order)"
                  size="x-small"
                  label
                  color="grey-darken-3"
                >
                  {{ orderState(order) }}
                </v-chip>
              </div>
              <ul
                v-if="order.lines.length"
                class="lines"
              >
                <li
                  v-for="line in order.lines"
                  :key="line.id"
                  class="mb-2"
                >
                  <div class="d-flex flex-wrap align-center ga-2">
                    <span class="text-body-2 font-weight-medium">
                      {{ line.productName ?? 'Producto sin nombre' }} ·
                      {{ quantityText(line.quantity, line.uom) }}
                    </span>
                    <v-chip
                      v-if="line.kind === 'package'"
                      size="x-small"
                      label
                    >
                      Paquete
                    </v-chip>
                    <v-chip
                      v-if="lineState(line)"
                      size="x-small"
                      label
                      color="warning"
                      prepend-icon="mdi-link-off"
                    >
                      {{ lineState(line) }}
                    </v-chip>
                  </div>
                  <ul
                    v-if="line.requirements.length"
                    class="requirements"
                    :aria-label="`Requerimientos que alimenta ${line.productName ?? 'la línea'}`"
                  >
                    <li
                      v-for="requirement in line.requirements"
                      :key="requirement.id"
                      class="text-body-2"
                    >
                      <v-icon
                        icon="mdi-arrow-right-bottom"
                        size="x-small"
                        class="mr-1"
                        aria-hidden="true"
                      />
                      {{ requirementText(requirement) }}
                      <span class="text-medium-emphasis">
                        ({{ formatRange(requirement.startsOn, requirement.endsOn) }})
                      </span>
                    </li>
                  </ul>
                  <div
                    v-else
                    class="requirements text-body-2 text-medium-emphasis"
                  >
                    No alimenta ningún requerimiento
                  </div>
                </li>
              </ul>
              <p
                v-else
                class="text-body-2 text-medium-emphasis"
              >
                Sin líneas de atención
              </p>
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <responsive-dialog
      :model-value="!!moving"
      title="Cambiar fecha de incorporación"
      :mobile-fullscreen="false"
      max-width="560"
      @update:model-value="moving = null"
    >
      <p class="text-body-1 mb-4">
        Kuido envía a Odoo las sesiones de este paciente desde esta fecha. Las de días anteriores
        se siguen registrando a mano en Odoo.
      </p>
      <v-text-field
        v-model="goLiveOn"
        type="date"
        :min="today"
        label="Kuido envía sus sesiones a Odoo desde"
        variant="outlined"
        density="compact"
        :error-messages="dateError"
      />
      <v-alert
        v-if="direction"
        :type="direction === 'later' ? 'warning' : 'info'"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ direction === 'later' ?
          'Las sesiones que Kuido ya envió para días anteriores a la nueva fecha quedarán ' +
          'canceladas en Odoo.' :
          'Kuido enviará también las sesiones de los turnos desde la nueva fecha.' }}
      </v-alert>
      <v-alert
        v-if="moveError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ moveError }}
      </v-alert>
      <template #actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="moving = null"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          :loading="isMoving"
          :disabled="!direction"
          @click="moving && move({ id: moving.id, date: goLiveOn })"
        >
          Guardar
        </v-btn>
      </template>
    </responsive-dialog>
  </div>
</template>
<style scoped>
.patients-search {
  max-width: 360px;
}

.lines {
  list-style: none;
  padding: 0;
}

.requirements {
  list-style: none;
  padding-left: 16px;
}
</style>
